import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { useState, useEffect, useCallback } from "react";
import { useRealtimeSubscription } from "@/hooks/useRealtimeSubscription";
import { supabase } from "@/lib/supabase";
import {
  LayoutDashboard,
  Store,
  Ticket,
  Lock,
  Unlock,
  Copy,
  XCircle,
  Clock,
  CheckCircle2,
  Menu,
  X,
  LogOut,
  Send,
  Users,
  CalendarDays,
  TrendingUp,
  Settings,
  Save,
  Megaphone,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { format, startOfMonth, endOfMonth } from "date-fns";
import { ptBR } from "date-fns/locale";

export const Route = createFileRoute("/admin")({
  beforeLoad: async ({ location }) => {
    const { data: { session } } = await supabase.auth.getSession();

    if (!session) {
      throw redirect({
        to: "/login",
        search: { redirect: location.href },
      });
    }

    const { data: user } = await supabase
      .from("usuarios")
      .select("role")
      .eq("id", session.user.id)
      .maybeSingle();

    if (!user || user.role !== "admin") {
      throw redirect({ to: "/" });
    }
  },
  component: AdminLayout,
});

function AdminLayout() {
  const [activeTab, setActiveTab] = useState<"overview" | "studios" | "invites" | "config">("overview");
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navigate = useNavigate();

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate({ to: "/login" });
  };

  const menuItems = [
    { id: "overview", label: "Visão Geral", icon: LayoutDashboard },
    { id: "studios", label: "Studios", icon: Store },
    { id: "invites", label: "Convites", icon: Ticket },
    { id: "config", label: "Configurações", icon: Settings },
  ];

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col md:flex-row">
      {/* Sidebar Mobile Header */}
      <div className="md:hidden flex items-center justify-between p-4 border-b border-border bg-card sticky top-0 z-50 shadow-sm">
        <div className="flex items-center gap-2">
          <h1 className="font-bold text-lg text-foreground">Painel Administrativo</h1>
          <Badge className="bg-primary hover:bg-primary text-[10px] h-5 text-white">ADMIN</Badge>
        </div>
        <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="text-foreground">
          {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Sidebar Desktop */}
      <aside className={cn(
        "fixed inset-y-0 left-0 z-40 w-64 bg-card border-r border-border transform transition-transform duration-300 ease-in-out md:relative md:translate-x-0 shadow-sm",
        isMobileMenuOpen ? "translate-x-0" : "-translate-x-full"
      )}>
        <div className="p-6 hidden md:block border-b border-border/50">
          <div className="flex items-center gap-2 mb-1">
            <h1 className="font-bold text-xl tracking-tight text-foreground">Admin Panel</h1>
            <Badge className="bg-primary hover:bg-primary text-[10px] text-white">ADMIN</Badge>
          </div>
          <p className="text-muted-foreground text-xs font-medium">Gestão do Sistema</p>
        </div>

        <nav className="p-4 space-y-2 mt-4">
          {menuItems.map((item) => (
            <button
              key={item.id}
              onClick={() => { setActiveTab(item.id as any); setIsMobileMenuOpen(false); }}
              className={cn(
                "w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all font-medium group",
                activeTab === item.id
                  ? "bg-red-50 text-primary shadow-sm"
                  : "text-muted-foreground hover:text-foreground hover:bg-muted"
              )}
            >
              <item.icon size={20} className={cn(
                activeTab === item.id ? "text-primary" : "text-muted-foreground group-hover:text-primary"
              )} />
              {item.label}
            </button>
          ))}
        </nav>

        <div className="absolute bottom-4 left-4 right-4">
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-muted-foreground hover:text-primary hover:bg-red-50 transition-all font-medium"
          >
            <LogOut size={20} />
            Sair
          </button>
        </div>
      </aside>

      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black/20 z-30 md:hidden backdrop-blur-sm"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      <main className="flex-1 p-4 md:p-8 lg:p-12 overflow-y-auto">
        <div className="max-w-6xl mx-auto">
          {activeTab === "overview" && <OverviewSection />}
          {activeTab === "studios" && <StudiosSection />}
          {activeTab === "invites" && <InvitesSection />}
          {activeTab === "config" && <ConfigSection />}
        </div>
      </main>
    </div>
  );
}

// ─── Visão Geral ────────────────────────────────────────────────────────────

function OverviewSection() {
  const [stats, setStats] = useState({
    studios: 0,
    pendingInvites: 0,
    usedInvites: 0,
    agendamentosMes: 0,
    taxaConversao: 0,
  });
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      const now = new Date();
      const start = format(startOfMonth(now), "yyyy-MM-dd");
      const end = format(endOfMonth(now), "yyyy-MM-dd");

      const [studios, pending, used, agendamentos] = await Promise.all([
        supabase.from("usuarios").select("id", { count: "exact", head: true }).eq("role", "studio").eq("status", "ativo"),
        supabase.from("convites").select("id", { count: "exact", head: true }).eq("usado", false),
        supabase.from("convites").select("id", { count: "exact", head: true }).eq("usado", true),
        supabase.from("agendamentos").select("id", { count: "exact", head: true }).gte("data", start).lte("data", end),
      ]);

      const total = (pending.count || 0) + (used.count || 0);
      const taxa = total > 0 ? Math.round(((used.count || 0) / total) * 100) : 0;

      setStats({
        studios: studios.count || 0,
        pendingInvites: pending.count || 0,
        usedInvites: used.count || 0,
        agendamentosMes: agendamentos.count || 0,
        taxaConversao: taxa,
      });
      setIsLoading(false);
    };
    fetchStats();
  }, []);

  const cards = [
    { label: "Studios Ativos", value: stats.studios, icon: Store, color: "text-primary", bg: "bg-primary/10" },
    { label: "Convites Pendentes", value: stats.pendingInvites, icon: Clock, color: "text-amber-600", bg: "bg-amber-100" },
    { label: "Convites Usados", value: stats.usedInvites, icon: CheckCircle2, color: "text-emerald-600", bg: "bg-emerald-100" },
    { label: "Agendamentos (mês)", value: stats.agendamentosMes, icon: CalendarDays, color: "text-blue-600", bg: "bg-blue-100" },
    { label: "Taxa de Conversão", value: `${stats.taxaConversao}%`, icon: TrendingUp, color: "text-violet-600", bg: "bg-violet-100" },
  ];

  return (
    <div className="space-y-8 animate-in fade-in duration-500">
      <div>
        <h2 className="text-3xl font-black tracking-tight text-foreground">Visão Geral</h2>
        <p className="text-muted-foreground mt-1">Status atual da plataforma</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {cards.map((c) => (
          <Card key={c.label} className="bg-card border-border p-6 hover:border-primary/30 transition-colors shadow-sm">
            <CardHeader className="p-0 pb-4">
              <p className="text-muted-foreground text-[10px] font-black uppercase tracking-[0.2em]">{c.label}</p>
            </CardHeader>
            <div className="flex items-end justify-between">
              <h3 className="text-5xl font-black text-foreground">{isLoading ? "..." : c.value}</h3>
              <div className={cn("size-12 rounded-2xl flex items-center justify-center", c.bg, c.color)}>
                <c.icon size={28} />
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}

// ─── Studios ─────────────────────────────────────────────────────────────────

function StudiosSection() {
  const [usuarios, setUsuarios] = useState<any[]>([]);
  const [tatuadoresPorStudio, setTatuadoresPorStudio] = useState<Record<string, number>>({});
  const [isLoading, setIsLoading] = useState(true);

  const fetchUsuarios = useCallback(async () => {
    setIsLoading(true);

    const [{ data: users }, { data: equipe }] = await Promise.all([
      supabase.from("usuarios").select("id, nome, email, status, role, created_at").neq("role", "admin").order("created_at", { ascending: false }),
      supabase.from("equipe").select("studio_id").eq("status", "ativo"),
    ]);

    const contagem: Record<string, number> = {};
    (equipe || []).forEach((e) => {
      contagem[e.studio_id] = (contagem[e.studio_id] || 0) + 1;
    });

    setUsuarios(users || []);
    setTatuadoresPorStudio(contagem);
    setIsLoading(false);
  }, []);

  useEffect(() => { fetchUsuarios(); }, [fetchUsuarios]);
  useRealtimeSubscription("usuarios", fetchUsuarios);

  const toggleStatus = async (userId: string, currentStatus: string) => {
    const newStatus = currentStatus === "ativo" ? "bloqueado" : "ativo";
    const { error } = await supabase.from("usuarios").update({ status: newStatus }).eq("id", userId);
    if (error) {
      toast.error("Erro ao atualizar status");
    } else {
      toast.success(`Usuário ${newStatus === "ativo" ? "desbloqueado" : "bloqueado"} com sucesso`);
      fetchUsuarios();
    }
  };

  return (
    <div className="space-y-8 animate-in fade-in duration-500">
      <div>
        <h2 className="text-3xl font-black tracking-tight text-foreground">Usuários Cadastrados</h2>
        <p className="text-muted-foreground mt-1">Todos os studios e tatuadores do sistema</p>
      </div>

      <div className="bg-card border border-border rounded-3xl overflow-hidden shadow-sm">
        {isLoading ? (
          <div className="p-12 text-center text-muted-foreground font-bold">Carregando usuários...</div>
        ) : usuarios.length === 0 ? (
          <div className="p-12 text-center text-muted-foreground font-bold italic">Nenhum usuário cadastrado</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-border bg-muted/30">
                  <th className="px-6 py-4 text-[10px] font-black text-muted-foreground uppercase tracking-widest">Nome / E-mail</th>
                  <th className="px-6 py-4 text-[10px] font-black text-muted-foreground uppercase tracking-widest">Tipo</th>
                  <th className="px-6 py-4 text-[10px] font-black text-muted-foreground uppercase tracking-widest">Tatuadores</th>
                  <th className="px-6 py-4 text-[10px] font-black text-muted-foreground uppercase tracking-widest">Cadastro</th>
                  <th className="px-6 py-4 text-[10px] font-black text-muted-foreground uppercase tracking-widest">Status</th>
                  <th className="px-6 py-4 text-[10px] font-black text-muted-foreground uppercase tracking-widest text-right">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {usuarios.map((u) => (
                  <tr key={u.id} className="hover:bg-muted/10 transition-colors group">
                    <td className="px-6 py-4">
                      <p className="font-bold text-foreground group-hover:text-primary transition-colors">{u.nome || "—"}</p>
                      <p className="text-xs text-muted-foreground">{u.email}</p>
                    </td>
                    <td className="px-6 py-4">
                      <Badge variant="outline" className="text-[10px] font-black uppercase">
                        {u.role === "tatuador" ? "Tatuador" : "Studio"}
                      </Badge>
                    </td>
                    <td className="px-6 py-4">
                      {u.role === "studio" ? (
                        <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                          <Users size={14} />
                          <span className="font-bold">{tatuadoresPorStudio[u.id] || 0}</span>
                        </div>
                      ) : (
                        <span className="text-muted-foreground text-xs">—</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <p className="text-sm text-muted-foreground">
                        {u.created_at ? format(new Date(u.created_at), "dd/MM/yyyy", { locale: ptBR }) : "—"}
                      </p>
                    </td>
                    <td className="px-6 py-4">
                      <Badge className={cn(
                        "text-[10px] font-black uppercase px-2 py-0.5",
                        u.status === "ativo" ? "bg-emerald-100 text-emerald-600" : "bg-red-100 text-red-600"
                      )}>
                        {u.status}
                      </Badge>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => toggleStatus(u.id, u.status)}
                        className={cn(
                          "h-9 px-3 gap-2 font-bold text-xs",
                          u.status === "ativo" ? "hover:bg-red-50 hover:text-red-600" : "hover:bg-emerald-50 hover:text-emerald-600"
                        )}
                      >
                        {u.status === "ativo" ? <><Lock size={14} /> Bloquear</> : <><Unlock size={14} /> Desbloquear</>}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Convites ────────────────────────────────────────────────────────────────

function InvitesSection() {
  const [invites, setInvites] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [emailConvite, setEmailConvite] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedLink, setGeneratedLink] = useState<string | null>(null);
  const [expiracaoDias, setExpiracaoDias] = useState(7);

  const fetchInvites = useCallback(async () => {
    setIsLoading(true);
    const [{ data }, { data: config }] = await Promise.all([
      supabase.from("convites").select("*").order("id", { ascending: false }),
      supabase.from("admin_config").select("expiracao_convite_dias").single(),
    ]);
    setInvites(data || []);
    if (config?.expiracao_convite_dias) setExpiracaoDias(config.expiracao_convite_dias);
    setIsLoading(false);
  }, []);

  useEffect(() => { fetchInvites(); }, [fetchInvites]);
  useRealtimeSubscription("convites", fetchInvites);

  const generateInvite = async () => {
    if (!emailConvite.trim()) {
      toast.error("Informe o e-mail do destinatário");
      return;
    }

    setIsGenerating(true);
    setGeneratedLink(null);
    try {
      const codigo = Math.random().toString(36).substring(2, 14).toUpperCase();
      const expiraEm = new Date();
      expiraEm.setDate(expiraEm.getDate() + expiracaoDias);

      const { error } = await supabase.from("convites").insert({
        codigo,
        tipo: "studio",
        email_destinatario: emailConvite.trim(),
        expira_em: expiraEm.toISOString(),
        usado: false,
      });

      if (error) {
        toast.error("Erro ao gerar convite: " + error.message);
        return;
      }

      const fullUrl = `${window.location.origin}/cadastro?convite=${codigo}`;
      setGeneratedLink(fullUrl);
      setEmailConvite("");

      try {
        await navigator.clipboard.writeText(fullUrl);
        toast.success("Convite gerado! Link copiado para a área de transferência.");
      } catch {
        toast.success("Convite gerado! Copie o link abaixo.");
      }

      fetchInvites();
    } finally {
      setIsGenerating(false);
    }
  };

  const cancelInvite = async (inviteId: string) => {
    const { error } = await supabase.from("convites").delete().eq("id", inviteId);
    if (error) {
      toast.error("Erro ao cancelar convite");
    } else {
      toast.success("Convite cancelado");
      fetchInvites();
    }
  };

  const getStatus = (invite: any) => {
    if (invite.usado) return { label: "Usado", color: "bg-emerald-100 text-emerald-600", icon: CheckCircle2 };
    if (new Date(invite.expira_em) < new Date()) return { label: "Expirado", color: "bg-muted text-muted-foreground", icon: XCircle };
    return { label: "Pendente", color: "bg-amber-100 text-amber-600", icon: Clock };
  };

  return (
    <div className="space-y-8 animate-in fade-in duration-500">
      <div>
        <h2 className="text-3xl font-black tracking-tight text-foreground">Convites</h2>
        <p className="text-muted-foreground mt-1">Gerencie os acessos ao sistema</p>
      </div>

      <div className="flex gap-3">
        <Input
          type="email"
          placeholder="E-mail do destinatário"
          value={emailConvite}
          onChange={(e) => setEmailConvite(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && generateInvite()}
          className="bg-card border-border text-foreground h-12 max-w-sm"
        />
        <Button
          onClick={generateInvite}
          disabled={isGenerating}
          className="bg-primary hover:bg-primary/90 text-white font-black px-6 h-12 shadow-lg shadow-primary/20 shrink-0"
        >
          <Send size={16} className="mr-2" />
          {isGenerating ? "Gerando..." : "Gerar Convite"}
        </Button>
      </div>

      {generatedLink && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-5 space-y-3">
          <p className="text-[10px] font-black text-emerald-700 uppercase tracking-widest">Link do convite gerado</p>
          <div className="flex items-center gap-3">
            <code className="flex-1 text-sm font-mono text-emerald-900 bg-white border border-emerald-200 rounded-xl px-4 py-3 break-all select-all">
              {generatedLink}
            </code>
            <button
              onClick={() => { navigator.clipboard.writeText(generatedLink).catch(() => {}); toast.success("Link copiado!"); }}
              className="shrink-0 size-10 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white flex items-center justify-center transition-colors"
            >
              <Copy size={16} />
            </button>
          </div>
          <p className="text-xs text-emerald-600">Envie este link para o convidado. Expira em {expiracaoDias} dias.</p>
        </div>
      )}

      <div className="bg-card border border-border rounded-3xl overflow-hidden shadow-sm">
        {isLoading ? (
          <div className="p-12 text-center text-muted-foreground font-bold">Carregando convites...</div>
        ) : invites.length === 0 ? (
          <div className="p-12 text-center text-muted-foreground font-bold italic">Nenhum convite gerado</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-border bg-muted/30">
                  <th className="px-6 py-4 text-[10px] font-black text-muted-foreground uppercase tracking-widest">Código / E-mail</th>
                  <th className="px-6 py-4 text-[10px] font-black text-muted-foreground uppercase tracking-widest">Status</th>
                  <th className="px-6 py-4 text-[10px] font-black text-muted-foreground uppercase tracking-widest">Expiração</th>
                  <th className="px-6 py-4 text-[10px] font-black text-muted-foreground uppercase tracking-widest text-right">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {invites.map((inv) => {
                  const status = getStatus(inv);
                  return (
                    <tr key={inv.id} className="hover:bg-muted/10 transition-colors">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2 mb-0.5">
                          <p className="font-mono font-bold text-foreground tracking-widest">{inv.codigo}</p>
                          <button
                            onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/cadastro?convite=${inv.codigo}`); toast.success("Link copiado!"); }}
                            className="text-muted-foreground hover:text-foreground transition-colors"
                          >
                            <Copy size={12} />
                          </button>
                        </div>
                        <p className="text-xs text-muted-foreground">{inv.email_destinatario || "—"}</p>
                      </td>
                      <td className="px-6 py-4">
                        <Badge className={cn("text-[10px] font-black uppercase px-2 py-0.5 gap-1.5", status.color)}>
                          <status.icon size={10} /> {status.label}
                        </Badge>
                      </td>
                      <td className="px-6 py-4">
                        <p className="text-sm text-muted-foreground">{format(new Date(inv.expira_em), "dd/MM/yyyy")}</p>
                      </td>
                      <td className="px-6 py-4 text-right">
                        {!inv.usado && (
                          <Button variant="ghost" size="sm" onClick={() => cancelInvite(inv.id)} className="h-9 w-9 p-0 hover:bg-red-50 hover:text-red-600">
                            <XCircle size={18} />
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Configurações ───────────────────────────────────────────────────────────

function ConfigSection() {
  const [expiracaoDias, setExpiracaoDias] = useState(7);
  const [bannerAtivo, setBannerAtivo] = useState(false);
  const [bannerTexto, setBannerTexto] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchConfig = async () => {
      const { data } = await supabase.from("admin_config").select("*").single();
      if (data) {
        setExpiracaoDias(data.expiracao_convite_dias ?? 7);
        setBannerAtivo(data.banner_ativo ?? false);
        setBannerTexto(data.banner_texto ?? "");
      }
      setIsLoading(false);
    };
    fetchConfig();
  }, []);

  const handleSave = async () => {
    setIsSaving(true);
    const { error } = await supabase.from("admin_config").update({
      expiracao_convite_dias: expiracaoDias,
      banner_ativo: bannerAtivo,
      banner_texto: bannerTexto,
    }).neq("id", "00000000-0000-0000-0000-000000000000");

    if (error) {
      toast.error("Erro ao salvar configurações: " + error.message);
    } else {
      toast.success("Configurações salvas!");
    }
    setIsSaving(false);
  };

  if (isLoading) return <div className="p-12 text-center text-muted-foreground font-bold">Carregando...</div>;

  return (
    <div className="space-y-8 animate-in fade-in duration-500">
      <div>
        <h2 className="text-3xl font-black tracking-tight text-foreground">Configurações do Sistema</h2>
        <p className="text-muted-foreground mt-1">Parâmetros globais da plataforma</p>
      </div>

      <div className="space-y-6 max-w-2xl">
        {/* Convites */}
        <div className="bg-card border border-border rounded-2xl p-6 space-y-4 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="size-10 rounded-xl bg-primary/10 flex items-center justify-center text-primary">
              <Ticket size={20} />
            </div>
            <div>
              <h3 className="font-black text-foreground">Convites</h3>
              <p className="text-xs text-muted-foreground">Configurações de expiração</p>
            </div>
          </div>
          <div className="space-y-2">
            <Label className="text-[10px] font-black text-muted-foreground uppercase tracking-widest">
              Expiração (dias)
            </Label>
            <Input
              type="number"
              min={1}
              max={365}
              value={expiracaoDias}
              onChange={(e) => setExpiracaoDias(Number(e.target.value))}
              className="w-32 bg-background border-border"
            />
            <p className="text-xs text-muted-foreground">Novos convites gerados expirarão em {expiracaoDias} dias.</p>
          </div>
        </div>

        {/* Banner Global */}
        <div className="bg-card border border-border rounded-2xl p-6 space-y-4 shadow-sm">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="size-10 rounded-xl bg-amber-100 flex items-center justify-center text-amber-600">
                <Megaphone size={20} />
              </div>
              <div>
                <h3 className="font-black text-foreground">Banner de Aviso</h3>
                <p className="text-xs text-muted-foreground">Exibido para todos os usuários logados</p>
              </div>
            </div>
            <Switch checked={bannerAtivo} onCheckedChange={setBannerAtivo} />
          </div>
          <div className="space-y-2">
            <Label className="text-[10px] font-black text-muted-foreground uppercase tracking-widest">
              Texto do aviso
            </Label>
            <Textarea
              placeholder="Ex: O sistema estará em manutenção no domingo das 00h às 02h."
              value={bannerTexto}
              onChange={(e) => setBannerTexto(e.target.value)}
              rows={3}
              className="bg-background border-border resize-none"
            />
          </div>
          {bannerAtivo && bannerTexto && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-sm text-amber-800 font-medium">
              <Megaphone size={14} className="inline mr-2" />
              {bannerTexto}
            </div>
          )}
        </div>

        <Button
          onClick={handleSave}
          disabled={isSaving}
          className="bg-primary hover:bg-primary/90 text-white font-black h-12 px-8 shadow-lg shadow-primary/20"
        >
          <Save size={16} className="mr-2" />
          {isSaving ? "Salvando..." : "Salvar Configurações"}
        </Button>
      </div>
    </div>
  );
}
